import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, picUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  BarChart3, Zap, Eye, MessageCircle, Share2,
  TrendingUp, TrendingDown, Trophy, Sparkles, DollarSign, Loader2,
  AlertTriangle, X, Wrench,
} from "lucide-react";
import {
  BarChart, Bar, ResponsiveContainer, XAxis, YAxis,
  Tooltip, Cell,
} from "recharts";
import RescueQueue from "@/components/admin/RescueQueue";
import LiveStats from "@/components/admin/LiveStats";

const fmtInt = (n) => new Intl.NumberFormat("en-US").format(Math.max(0, Math.floor(n || 0)));
const fmtSec = (ms) => {
  const s = Math.max(0, Math.floor((ms || 0) / 1000));
  const m = Math.floor(s / 60);
  return m > 0 ? `${m}m ${s % 60}s` : `${s}s`;
};
const fmtUsd = (cents) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" })
    .format((cents || 0) / 100);
const shortCaption = (c) => (c || "").replace(/\s+/g, " ").trim().slice(0, 48);

/**
 * One-tap "Repair failed videos" sweep. Drains the failed/stuck queue in
 * 25-video batches, showing live progress. Idempotent — user can stop
 * mid-sweep, or hit it again after a fresh batch of failures appears.
 *
 * Server-side pagination via `POST /admin/videos/repair-failed?batch_size=25`
 * — each call returns `{queued_normalize, queued_hls, total_remaining}` and
 * we keep firing until the total drops to zero.
 */
const RepairFailedButton = ({ runningLabel = "Repair failed videos", testId, onRefreshStatus }) => {
  const [running, setRunning] = useState(false);
  const [remaining, setRemaining] = useState(null);
  const [processed, setProcessed] = useState(0);
  const [startTotal, setStartTotal] = useState(0);
  const [msg, setMsg] = useState(null);
  const [cancelRequested, setCancelRequested] = useState(false);

  // Kick a peek on mount and after every batch so the count stays live.
  useEffect(() => {
    if (running) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/admin/videos/repair-failed/count");
        if (!cancelled) setRemaining(r.data?.total_remaining ?? 0);
      } catch {
        /* non-fatal */
      }
    })();
    return () => { cancelled = true; };
  }, [running]);

  const start = async () => {
    // Peek first so we can quote the queue size in the confirm.
    let initial = remaining;
    try {
      const r = await api.get("/admin/videos/repair-failed/count");
      initial = r.data?.total_remaining ?? 0;
      setRemaining(initial);
    } catch {
      /* fall through — we'll surface the API error on first batch call */
    }
    if (!initial) {
      toast.success("Nothing to repair — the queue is empty");
      return;
    }
    if (!window.confirm(
      `Repair ${initial} failed / stuck video(s)? This will reset their retry counters and re-queue them in batches of 25. You can stop mid-sweep.`
    )) return;

    setRunning(true);
    setCancelRequested(false);
    setProcessed(0);
    setStartTotal(initial);
    setMsg({ ok: true, text: `⚙ Starting sweep on ${initial} video(s)…` });

    let done = false;
    let batches = 0;
    while (!done) {
      // Cancel check runs at the top of the loop so the current batch
      // still gets to finish + audit-log before we bail.
      if (cancelRequested) {
        setMsg({ ok: true, text: `Sweep stopped after ${batches} batch(es) · ${processed} video(s) repaired` });
        break;
      }
      try {
        const r = await api.post("/admin/videos/repair-failed", null, { params: { batch_size: 25 } });
        const d = r.data || {};
        const thisBatch = (d.queued_normalize || 0) + (d.queued_hls || 0);
        batches += 1;
        setProcessed((p) => p + thisBatch);
        setRemaining(d.total_remaining ?? 0);
        setMsg({
          ok: true,
          text: `⚙ Batch ${batches} · +${d.queued_normalize} normalize / +${d.queued_hls} hls · ${d.total_remaining} remaining`,
        });
        if (thisBatch === 0) {
          // No progress this batch means every remaining doc is
          // either already normalizing or has no storage_path — both
          // states the server correctly refuses. Stop the loop so we
          // don't spin.
          done = true;
        } else if ((d.total_remaining ?? 0) === 0) {
          done = true;
        }
        // Small breathing gap so the backend has a chance to actually
        // start the workers and the count query on the next iteration
        // reflects reality.
        await new Promise((res) => setTimeout(res, 1200));
      } catch (e) {
        setMsg({ ok: false, text: e?.response?.data?.detail || "Sweep failed" });
        break;
      }
    }
    setRunning(false);
    if (onRefreshStatus) {
      // Give the workers a moment then reload the HLS status card.
      setTimeout(() => onRefreshStatus(), 4000);
    }
  };

  const stop = () => setCancelRequested(true);

  const progressPct = startTotal > 0 && running
    ? Math.min(100, Math.round((processed / startTotal) * 100))
    : 0;

  return (
    <div>
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-widest text-white/50">
            // repair everything failed & stuck
          </p>
          <p className="text-white/70 text-sm mt-1">
            {remaining === null ? "Loading count…" :
             remaining === 0 ? "Queue is empty — no failed videos" :
             `${remaining} video(s) waiting to be rescued`}
          </p>
        </div>
        {running ? (
          <button
            onClick={stop}
            data-testid={`${testId}-stop`}
            className="font-mono text-[10px] uppercase tracking-widest text-white bg-[#FF3B30] hover:bg-[#FF3B30]/80 px-3 py-1.5 transition-all"
          >
            stop
          </button>
        ) : (
          <button
            onClick={start}
            disabled={running || remaining === 0}
            data-testid={testId}
            className="font-mono text-[10px] uppercase tracking-widest text-black bg-[#39FF14] hover:shadow-[0_0_10px_rgba(57,255,20,0.6)] disabled:opacity-40 disabled:cursor-not-allowed px-3 py-1.5 transition-shadow"
          >
            {runningLabel}
          </button>
        )}
      </div>
      {running && (
        <div className="mt-3">
          <div className="h-1 bg-white/10 relative overflow-hidden">
            <div
              className="absolute inset-y-0 left-0 bg-[#39FF14] transition-[width]"
              style={{ width: `${progressPct}%`, boxShadow: "0 0 10px rgba(57,255,20,0.55)" }}
              data-testid={`${testId}-progress-bar`}
            />
          </div>
          <p className="mt-1 font-mono text-[9px] uppercase tracking-widest text-white/40">
            {processed}/{startTotal} · {progressPct}%
          </p>
        </div>
      )}
      {msg && (
        <p
          data-testid={`${testId}-status`}
          className={`mt-2 font-mono text-[10px] uppercase tracking-widest ${
            msg.ok ? "text-[#39FF14]" : "text-[#FF3B30]"
          }`}
        >
          {msg.text}
        </p>
      )}
    </div>
  );
};

// A compact table row for a video with author avatar + caption preview.
const VideoRow = ({ r, right, subtitle }) => (
  <Link
    to={`/v/${r.video_id || r.id}`}
    className="grid grid-cols-[24px_1fr_auto] gap-3 items-center py-2 border-b border-white/5 hover:bg-white/[0.02] transition-colors"
    data-testid={`analytics-video-row-${r.video_id || r.id}`}
  >
    {r.author?.picture ? (
      <img
        src={picUrl(r.author.picture)}
        alt=""
        className="h-6 w-6 rounded-full object-cover border border-white/15"
      />
    ) : (
      <div className="h-6 w-6 rounded-full bg-[#FF007F]/20 border border-[#FF007F]/40" />
    )}
    <div className="min-w-0">
      <p className="text-white text-xs truncate">
        {shortCaption(r.caption) || <em className="text-white/40">no caption</em>}
      </p>
      <p className="font-mono text-[10px] text-white/40 truncate">
        @{r.author?.username || r.author?.name || "unknown"}
        {subtitle ? <span className="ml-2 text-white/30">{subtitle}</span> : null}
      </p>
    </div>
    <div className="font-mono text-[11px] text-[#39FF14] whitespace-nowrap">{right}</div>
  </Link>
);

const SectionCard = ({ title, icon: Icon, accent = "#00FFFF", children, empty }) => (
  <section className="bg-[#0A0A0C] border border-white/10 p-5">
    <div className="flex items-center gap-2 mb-4">
      <Icon className="h-4 w-4" style={{ color: accent }} />
      <h2 className="font-mono text-[11px] uppercase tracking-widest text-white/80">
        {title}
      </h2>
    </div>
    {children || (
      <p className="text-white/30 font-mono text-[10px] uppercase tracking-widest py-6 text-center">
        {empty || "no data yet"}
      </p>
    )}
  </section>
);

const KpiCard = ({ label, value, sub, accent = "#39FF14", icon: Icon }) => (
  <div className="bg-[#0A0A0C] border border-white/10 p-4 flex items-start gap-3">
    <div
      className="h-9 w-9 border flex items-center justify-center flex-shrink-0"
      style={{ borderColor: accent + "60", color: accent }}
    >
      <Icon className="h-4 w-4" />
    </div>
    <div className="min-w-0">
      <p className="font-mono text-[9px] uppercase tracking-widest text-white/40">
        {label}
      </p>
      <p className="font-display font-black text-2xl text-white truncate">{value}</p>
      {sub ? (
        <p className="font-mono text-[10px] text-white/50 truncate">{sub}</p>
      ) : null}
    </div>
  </div>
);

const CATEGORY_COLORS = ["#FF007F", "#00FFFF", "#39FF14", "#FF8A00", "#B14BFF", "#FFD700"];

export default function AdminAnalytics() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(7);
  const [err, setErr] = useState("");
  const [notifs, setNotifs] = useState([]);
  const [cacheStats, setCacheStats] = useState(null);
  const [backfilling, setBackfilling] = useState(false);
  const [backfillResult, setBackfillResult] = useState(null);
  const [giftSettings, setGiftSettings] = useState(null);
  const [giftPctInput, setGiftPctInput] = useState("");
  const [savingGift, setSavingGift] = useState(false);
  const [normalizeList, setNormalizeList] = useState([]);
  const [normalizingId, setNormalizingId] = useState(null);
  const [normalizeMsg, setNormalizeMsg] = useState(null);
  const [fixingAll, setFixingAll] = useState(false);
  const [hlsStatus, setHlsStatus] = useState(null);
  const [hlsBackfilling, setHlsBackfilling] = useState(false);
  const [hlsMsg, setHlsMsg] = useState(null);

  const loadHlsStatus = async () => {
    try {
      const r = await api.get("/admin/videos/hls-status");
      setHlsStatus(r.data);
    } catch {
      /* non-fatal */
    }
  };

  const backfillHls = async ({ includeFailed = false } = {}) => {
    if (hlsBackfilling) return;
    if (!window.confirm(`Queue HLS generation for up to 50 pending videos${includeFailed ? " (including failed retries)" : ""}?`)) return;
    setHlsBackfilling(true);
    setHlsMsg(null);
    try {
      const r = await api.post("/admin/videos/backfill-hls", null, {
        params: { limit: 50, include_failed: includeFailed },
      });
      setHlsMsg({
        ok: true,
        text: `⚙ ${r.data?.queued ?? 0} video(s) queued. Max 5 running concurrently — refresh status in a few minutes.`,
      });
      setTimeout(() => { loadHlsStatus(); }, 30000);
    } catch (e) {
      setHlsMsg({
        ok: false,
        text: e?.response?.data?.detail || "Could not queue HLS backfill",
      });
    } finally {
      setHlsBackfilling(false);
    }
  };

  const loadCacheStats = async () => {
    try {
      const r = await api.get("/admin/cache-stats");
      setCacheStats(r.data);
    } catch {
      // Non-fatal.
    }
  };

  const loadNotifs = async () => {
    try {
      const r = await api.get("/admin/notifications");
      setNotifs(r.data?.notifications || []);
    } catch {
      // Non-fatal — dashboard still renders without the alerts strip.
    }
  };

  const dismissNotif = async (id) => {
    // Optimistic: drop from local state, then hit the API.
    setNotifs((prev) => prev.filter((n) => n.id !== id));
    try {
      await api.post(`/admin/notifications/${id}/dismiss`);
    } catch {
      // If the API call failed, reload to resync.
      loadNotifs();
    }
  };

  const runBackfill = async () => {
    if (backfilling) return;
    if (
      !window.confirm(
        "Initialize missing money/counter fields (wallet_balance_cents, coins_balance, etc.) to 0 on every user document?\n\nSafe & idempotent — running again does nothing."
      )
    ) {
      return;
    }
    setBackfilling(true);
    setBackfillResult(null);
    try {
      const r = await api.post("/admin/backfill/user-fields");
      setBackfillResult(r.data);
    } catch (e) {
      setBackfillResult({ error: e?.response?.data?.detail || "Backfill failed" });
    } finally {
      setBackfilling(false);
    }
  };

  const loadGiftSettings = async () => {
    try {
      const r = await api.get("/admin/settings/gifts");
      setGiftSettings(r.data);
      setGiftPctInput(String(r.data.creator_pct));
    } catch {
      /* non-fatal — retry on next load */
    }
  };

  const loadNormalizeList = async () => {
    try {
      const r = await api.get("/admin/videos/needs-normalize", { params: { limit: 50 } });
      setNormalizeList(r.data?.videos || []);
    } catch {
      /* non-fatal */
    }
  };

  useEffect(() => {
    loadGiftSettings();
    loadNormalizeList();
    loadHlsStatus();
  }, []);

  const saveGiftSplit = async () => {
    const pct = parseInt(giftPctInput, 10);
    if (!Number.isFinite(pct)) {
      toast.error("Enter a number");
      return;
    }
    if (savingGift) return;
    setSavingGift(true);
    try {
      const r = await api.post("/admin/settings/gifts", { creator_pct: pct });
      setGiftSettings((prev) => ({ ...(prev || {}), ...r.data }));
      setGiftPctInput(String(r.data.creator_pct));
      toast.success(`Split saved · ${r.data.creator_pct}/${r.data.platform_pct}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save split");
    } finally {
      setSavingGift(false);
    }
  };

  const normalizeVideo = async (videoId) => {
    if (normalizingId) return;
    setNormalizingId(videoId);
    setNormalizeMsg(null);
    try {
      const r = await api.post(`/admin/videos/${videoId}/normalize`);
      setNormalizeMsg({
        ok: true,
        text: `⚙ ${videoId.slice(0, 8)}… queued for re-encode. Refresh in ~30–90s.`,
      });
      // Optimistically flip render_status in the local list so the row
      // shows NORMALIZING immediately and the FIX button disables.
      setNormalizeList((prev) =>
        prev.map((v) => (v.id === videoId ? { ...v, render_status: "normalizing" } : v))
      );
      // Auto-refresh the list once, giving the worker a head start.
      setTimeout(() => { loadNormalizeList(); }, 15000);
    } catch (e) {
      setNormalizeMsg({
        ok: false,
        text: e?.response?.data?.detail || "Could not queue re-encode",
      });
    } finally {
      setNormalizingId(null);
    }
  };

  const fixAllBroken = async () => {
    if (fixingAll) return;
    // Cheap guard rail — bulk actions deserve a quick sanity check.
    if (!window.confirm(`Queue re-encode for ${normalizeList.length} broken video(s)?`)) return;
    setFixingAll(true);
    setNormalizeMsg(null);
    try {
      const r = await api.post("/admin/videos/normalize-all", null, { params: { limit: 200 } });
      setNormalizeMsg({
        ok: true,
        text: `⚙ ${r.data?.queued ?? 0} video(s) queued. Max 5 running concurrently — refresh in ~1–2 min.`,
      });
      // Optimistically flip everything to normalizing in the local list.
      setNormalizeList((prev) =>
        prev.map((v) =>
          v.render_status === "normalizing" ? v : { ...v, render_status: "normalizing" }
        )
      );
      setTimeout(() => { loadNormalizeList(); }, 20000);
    } catch (e) {
      setNormalizeMsg({
        ok: false,
        text: e?.response?.data?.detail || "Could not queue bulk re-encode",
      });
    } finally {
      setFixingAll(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setErr("");
      try {
        const r = await api.get(`/admin/analytics?days=${days}&top=8`);
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || "Failed to load analytics");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    loadNotifs();
    loadCacheStats();
    return () => { cancelled = true; };
  }, [days]);

  if (!user) {
    return (
      <div className="p-8 text-white/60 font-mono text-xs">Sign in to view admin analytics.</div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white p-6 sm:p-10">
      <div className="max-w-7xl mx-auto space-y-6">
        <header className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-[#FF007F] mb-1">
              // admin.analytics
            </p>
            <h1 className="font-display font-black text-3xl sm:text-4xl text-white">
              Algorithm Dashboard
            </h1>
            <p className="text-white/50 text-sm mt-1">
              Real-time view of watch time, engagement, boost revenue & viral signals from the recommendation algorithm.
            </p>
          </div>
          <div className="flex items-center gap-2" data-testid="analytics-window-toggle">
            {[1, 7, 30].map((d) => (
              <button
                key={d}
                onClick={() => setDays(d)}
                className={`font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 border transition-colors ${
                  d === days
                    ? "border-[#00FFFF] text-[#00FFFF]"
                    : "border-white/15 text-white/50 hover:text-white"
                }`}
              >
                {d}d
              </button>
            ))}
          </div>
        </header>

        {err && (
          <div className="border border-[#FF3B30]/50 text-[#FF3B30] font-mono text-xs p-3">
            {err}
          </div>
        )}

        {/* Ops alerts — auto-fired when a video crosses the ×0.10 stream-health tier */}
        {notifs.length > 0 && (
          <section data-testid="admin-ops-alerts" className="border border-[#FFB800]/40 bg-[#FFB800]/[0.04] p-4">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="h-4 w-4 text-[#FFB800]" strokeWidth={2.5} />
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FFB800]">
                // ops.alerts · {notifs.length}
              </p>
            </div>
            <ul className="space-y-2">
              {notifs.map((n) => (
                <li
                  key={n.id}
                  data-testid={`admin-notif-${n.id}`}
                  className="flex items-start gap-3 border border-white/10 bg-black/40 p-3"
                >
                  <div className="h-8 w-8 flex-shrink-0 bg-[#FF3B30]/15 border border-[#FF3B30]/40 flex items-center justify-center">
                    <AlertTriangle className="h-4 w-4 text-[#FF3B30]" strokeWidth={2.5} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-display font-bold text-sm text-white">
                      {n.caption ? `"${shortCaption(n.caption)}"` : "Broken video hidden from feed"}
                    </p>
                    <p className="text-white/60 text-xs mt-0.5">
                      {n.message}
                    </p>
                    <div className="flex items-center gap-3 mt-1.5">
                      {n.video_id && (
                        <Link
                          to={`/v/${n.video_id}`}
                          className="font-mono text-[10px] uppercase tracking-widest text-[#00FFFF] hover:underline"
                          data-testid={`admin-notif-open-${n.id}`}
                        >
                          open video →
                        </Link>
                      )}
                      <span className="font-mono text-[10px] uppercase tracking-widest text-white/30">
                        {new Date(n.created_at).toLocaleString()}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => dismissNotif(n.id)}
                    data-testid={`admin-notif-dismiss-${n.id}`}
                    className="h-6 w-6 flex-shrink-0 flex items-center justify-center text-white/40 hover:text-white hover:bg-white/10 transition-colors"
                    aria-label="Dismiss"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Pod cache health tile — quick ops glance without SSHing */}
        {cacheStats && (
          <section data-testid="admin-cache-health" className="border border-white/10 bg-[#0A0A0C] p-4">
            <div className="flex items-center justify-between mb-3">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">
                // pod.cache
              </p>
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/30">
                {cacheStats.last_swept_at
                  ? `swept ${Math.round(cacheStats.seconds_since_sweep || 0)}s ago`
                  : `live scan`}
              </p>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <p className="font-mono text-[9px] uppercase tracking-widest text-white/40 mb-1">utilization</p>
                <p
                  className={`font-display font-black text-2xl ${
                    cacheStats.utilization_pct > 85
                      ? "text-[#FF3B30]"
                      : cacheStats.utilization_pct > 60
                      ? "text-[#FFB800]"
                      : "text-[#39FF14]"
                  }`}
                  data-testid="cache-utilization-pct"
                >
                  {cacheStats.utilization_pct}%
                </p>
                <p className="font-mono text-[9px] uppercase tracking-widest text-white/30 mt-0.5">
                  {(cacheStats.bytes / (1024 * 1024)).toFixed(1)} / {(cacheStats.cap_bytes / (1024 * 1024)).toFixed(0)} MB
                </p>
              </div>
              <div>
                <p className="font-mono text-[9px] uppercase tracking-widest text-white/40 mb-1">files</p>
                <p className="font-display font-black text-2xl text-white" data-testid="cache-completed-count">
                  {cacheStats.completed}
                </p>
                <p className="font-mono text-[9px] uppercase tracking-widest text-white/30 mt-0.5">cached</p>
              </div>
              <div>
                <p className="font-mono text-[9px] uppercase tracking-widest text-white/40 mb-1">.part</p>
                <p
                  className={`font-display font-black text-2xl ${
                    cacheStats.parts > 5 ? "text-[#FF3B30]" : "text-white"
                  }`}
                  data-testid="cache-parts-count"
                >
                  {cacheStats.parts}
                </p>
                <p className="font-mono text-[9px] uppercase tracking-widest text-white/30 mt-0.5">in-flight</p>
              </div>
            </div>
          </section>
        )}

        {/* Data-integrity utility — initialize missing money/counter fields to 0
            on all user docs. Prevents whole classes of "missing != 0" filter
            bugs (see: earnings tip 402 regression). Safe & idempotent. */}
        <section
          data-testid="admin-data-integrity"
          className="border border-white/10 bg-[#0A0A0C] p-4"
        >
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 mb-1">
                // data.integrity
              </p>
              <p className="text-white text-sm mb-1">
                Backfill missing user fields
              </p>
              <p className="font-mono text-[10px] text-white/40 leading-snug max-w-xl">
                Sets{" "}
                <span className="text-[#00FFFF]">wallet_balance_cents</span>,{" "}
                <span className="text-[#00FFFF]">coins_balance</span>,{" "}
                <span className="text-[#00FFFF]">coins_lifetime</span>,{" "}
                <span className="text-[#00FFFF]">lifetime_gifted_cents</span>,{" "}
                <span className="text-[#00FFFF]">lifetime_purchased_cents</span>{" "}
                and follower/following/video counts to 0 on every user document
                where the field is missing. Idempotent — safe to run any time.
              </p>
            </div>
            <button
              onClick={runBackfill}
              disabled={backfilling}
              data-testid="admin-backfill-btn"
              className="shrink-0 flex items-center gap-2 bg-[#39FF14] text-black font-bold uppercase tracking-widest text-[11px] px-4 py-2.5 hover:shadow-[0_0_20px_rgba(57,255,20,0.5)] disabled:opacity-50 transition-all"
            >
              {backfilling ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> running…
                </>
              ) : (
                <>
                  <Wrench className="h-3.5 w-3.5" /> run backfill
                </>
              )}
            </button>
          </div>

          {backfillResult && !backfillResult.error && (
            <div
              data-testid="admin-backfill-result"
              className="mt-4 border border-[#39FF14]/40 bg-[#39FF14]/5 p-3"
            >
              <p className="font-mono text-[10px] uppercase tracking-widest text-[#39FF14] mb-2">
                // done · {backfillResult.total_modified} field-writes · {backfillResult.duration_ms}ms
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-1">
                {Object.entries(backfillResult.per_field || {}).map(([k, v]) => (
                  <p
                    key={k}
                    className="font-mono text-[10px] text-white/60 truncate"
                  >
                    <span className="text-white/40">{k}</span>{" "}
                    <span className={v > 0 ? "text-[#39FF14]" : "text-white/30"}>
                      +{v}
                    </span>
                  </p>
                ))}
              </div>
              <p className="font-mono text-[9px] uppercase tracking-widest text-white/30 mt-2">
                {backfillResult.total_users} users scanned
              </p>
            </div>
          )}

          {backfillResult?.error && (
            <div
              data-testid="admin-backfill-error"
              className="mt-4 border border-[#FF3B30]/40 bg-[#FF3B30]/5 p-3 font-mono text-[11px] text-[#FF3B30]"
            >
              {backfillResult.error}
            </div>
          )}
        </section>

        {/* Comment-gift split — live-tunable creator/platform ratio. */}
        <section
          data-testid="admin-gift-settings"
          className="border border-white/10 bg-[#0A0A0C] p-4"
        >
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0 flex-1">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 mb-1">
                // gift.split
              </p>
              <p className="text-white text-sm mb-1">
                Comment-gift creator / platform ratio
              </p>
              <p className="font-mono text-[10px] text-white/40 leading-snug max-w-xl">
                Live-tunable — every new comment-gift uses this split immediately
                (cache flushed on save). Boost/tip 50/50 split is unaffected.
                Allowed range: {giftSettings?.min ?? 10}% – {giftSettings?.max ?? 95}%.
              </p>
            </div>
            {giftSettings && (
              <div className="flex flex-col items-end gap-1 shrink-0">
                <p className="font-display font-black text-3xl text-[#39FF14] leading-none">
                  {giftSettings.creator_pct}
                  <span className="text-white/30 text-lg">/{giftSettings.platform_pct}</span>
                </p>
                <p className="font-mono text-[9px] uppercase tracking-widest text-white/40">
                  creator / platform
                </p>
              </div>
            )}
          </div>

          <div className="mt-4 flex items-center gap-2 flex-wrap">
            <label className="font-mono text-[10px] uppercase tracking-widest text-white/40">
              creator %
            </label>
            <input
              type="number"
              min={giftSettings?.min ?? 10}
              max={giftSettings?.max ?? 95}
              value={giftPctInput}
              onChange={(e) => setGiftPctInput(e.target.value.replace(/[^0-9]/g, ""))}
              data-testid="gift-split-input"
              className="w-24 bg-white/5 border border-white/10 focus:border-[#39FF14] outline-none px-3 py-2 font-mono text-sm text-white text-center"
            />
            <span className="font-mono text-[10px] text-white/40">
              / {giftPctInput ? 100 - (parseInt(giftPctInput, 10) || 0) : "…"} platform
            </span>
            <button
              onClick={saveGiftSplit}
              disabled={savingGift || !giftPctInput}
              data-testid="gift-split-save"
              className="ml-auto sm:ml-2 flex items-center gap-2 bg-[#FF007F] text-black font-bold uppercase tracking-widest text-[11px] px-4 py-2 hover:shadow-[0_0_20px_rgba(255,0,127,0.5)] disabled:opacity-50 transition-all"
            >
              {savingGift ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> saving…
                </>
              ) : (
                "save split"
              )}
            </button>
          </div>

          {/* Simulator: shows what a 500-coin gift looks like under the pending value */}
          {giftPctInput && giftSettings && (
            <div
              data-testid="gift-split-preview"
              className="mt-3 font-mono text-[10px] text-white/50 leading-snug"
            >
              // example: 500-coin gift →{" "}
              <span className="text-[#39FF14]">
                {Math.floor((500 * (parseInt(giftPctInput, 10) || 0)) / 100)} coins
                ($
                {(
                  (Math.floor((500 * (parseInt(giftPctInput, 10) || 0)) / 100)) *
                  0.01
                ).toFixed(2)}
                ) to creator
              </span>
              {" · "}
              <span className="text-white/60">
                {500 - Math.floor((500 * (parseInt(giftPctInput, 10) || 0)) / 100)} coins
                ($
                {(
                  (500 - Math.floor((500 * (parseInt(giftPctInput, 10) || 0)) / 100)) *
                  0.01
                ).toFixed(2)}
                ) to platform
              </span>
            </div>
          )}
        </section>

        {/* Video transcoding backfill — surfaces broken videos so admins can
            re-encode them one at a time to web-safe H.264/AAC-LC. */}
        <section
          data-testid="admin-transcode-backfill"
          className="border border-white/10 bg-[#0A0A0C] p-4"
        >
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0 flex-1">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 mb-1">
                // video.normalize
              </p>
              <p className="text-white text-sm mb-1">
                Videos flagged as unplayable
              </p>
              <p className="font-mono text-[10px] text-white/40 leading-snug max-w-xl">
                Any video with{" "}
                <span className="text-[#FF3B30]">stream_broken</span> or a
                non-web-safe codec probe (HE-AAC / HEVC etc.) shows up here.
                Click <span className="text-[#39FF14]">FIX</span> to{" "}
                <span className="text-[#FFB800]">queue</span> a background
                re-encode to H.264 + AAC-LC + faststart. The job takes 30–90s
                for most clips — hit <span className="text-white/70">refresh</span> to
                see when it clears.
                <br />
                <span className="text-[#39FF14]/70">
                  // auto-heal daemon: sweeps every hour, retries up to 3× per video.
                </span>
              </p>
            </div>
            <div className="flex shrink-0 gap-2">
              <button
                onClick={fixAllBroken}
                disabled={fixingAll || normalizeList.length === 0}
                data-testid="normalize-fix-all"
                className="flex items-center gap-1.5 bg-[#39FF14] text-black font-bold uppercase tracking-widest text-[10px] px-3 py-1.5 hover:shadow-[0_0_15px_rgba(57,255,20,0.5)] disabled:opacity-40 disabled:cursor-not-allowed transition-all"
              >
                {fixingAll ? (
                  <>
                    <Loader2 className="h-3 w-3 animate-spin" /> queueing…
                  </>
                ) : (
                  <>
                    <Wrench className="h-3 w-3" /> fix all
                  </>
                )}
              </button>
              <button
                onClick={loadNormalizeList}
                data-testid="normalize-refresh"
                className="font-mono text-[10px] uppercase tracking-widest text-white/40 hover:text-white border border-white/10 hover:border-white/30 px-3 py-1.5 transition-all"
              >
                refresh
              </button>
            </div>
          </div>

          {normalizeMsg && (
            <div
              data-testid="normalize-msg"
              className={`mt-3 p-2 font-mono text-[11px] border ${
                normalizeMsg.ok
                  ? "border-[#39FF14]/40 bg-[#39FF14]/5 text-[#39FF14]"
                  : "border-[#FF3B30]/40 bg-[#FF3B30]/5 text-[#FF3B30]"
              }`}
            >
              {normalizeMsg.text}
            </div>
          )}

          <div className="mt-3">
            {normalizeList.length === 0 ? (
              <p
                data-testid="normalize-empty"
                className="font-mono text-[10px] uppercase tracking-widest text-white/30 py-4"
              >
                // no broken videos — you are clean.
              </p>
            ) : (
              <div className="space-y-1">
                {normalizeList.map((v) => {
                  const isNormalizing = v.render_status === "normalizing";
                  const busy = normalizingId === v.id || isNormalizing;
                  const beforeAudio =
                    v.video_probe?.before?.audio_profile ||
                    v.video_probe?.before?.audio_codec ||
                    "?";
                  const beforeVideo =
                    v.video_probe?.before?.video_codec || "?";
                  return (
                    <div
                      key={v.id}
                      data-testid={`normalize-row-${v.id}`}
                      className="flex items-center gap-3 py-2 px-2 border border-white/5 hover:border-white/20 transition-colors"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="font-mono text-[11px] text-white truncate">
                          {v.id}
                        </p>
                        <p className="font-mono text-[9px] uppercase tracking-widest text-white/40">
                          {beforeVideo} · {beforeAudio} ·{" "}
                          {v.size ? `${Math.round(v.size / 1024 / 1024)}MB` : "?MB"}{" "}
                          · {v.view_count ?? 0} views
                          {isNormalizing && (
                            <span className="text-[#FFB800] ml-2">normalizing…</span>
                          )}
                          {!isNormalizing && v.stream_broken && (
                            <span className="text-[#FF3B30] ml-2">broken</span>
                          )}
                        </p>
                      </div>
                      <button
                        onClick={() => normalizeVideo(v.id)}
                        disabled={busy}
                        data-testid={`normalize-fix-${v.id}`}
                        className="shrink-0 flex items-center gap-1.5 bg-[#39FF14] text-black font-bold uppercase tracking-widest text-[10px] px-3 py-1.5 hover:shadow-[0_0_15px_rgba(57,255,20,0.5)] disabled:opacity-50 transition-all"
                      >
                        {busy ? (
                          <>
                            <Loader2 className="h-3 w-3 animate-spin" />{" "}
                            {isNormalizing ? "working…" : "queueing…"}
                          </>
                        ) : (
                          <>
                            <Wrench className="h-3 w-3" /> fix
                          </>
                        )}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </section>

        {/* -----------------------  HLS backfill tile  ----------------------- */}
        <section
          data-testid="hls-backfill-section"
          className="border border-white/5 hover:border-white/10 transition-colors bg-black/40 p-4"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="font-mono text-[10px] uppercase tracking-widest text-white/40 mb-1">
                // hls.ladder
              </p>
              <p data-testid="hls-title" className="font-display font-bold text-lg text-white">
                Adaptive streaming (HLS)
              </p>
              <p className="font-mono text-[10px] text-white/40 leading-snug max-w-xl">
                Every normalized video gets a{" "}
                <span className="text-[#39FF14]">360p / 720p / 1080p</span> HLS
                ladder so hls.js switches quality on the fly. New uploads
                auto-generate. Use{" "}
                <span className="text-[#39FF14]">backfill</span> to fill in
                older clips that pre-date this feature.
              </p>
            </div>
            <div className="flex shrink-0 gap-2">
              <button
                onClick={() => backfillHls({ includeFailed: false })}
                disabled={hlsBackfilling || !hlsStatus?.pending}
                data-testid="hls-backfill-btn"
                className="flex items-center gap-1.5 bg-[#39FF14] text-black font-bold uppercase tracking-widest text-[10px] px-3 py-1.5 hover:shadow-[0_0_15px_rgba(57,255,20,0.5)] disabled:opacity-40 disabled:cursor-not-allowed transition-all"
              >
                {hlsBackfilling ? (
                  <>
                    <Loader2 className="h-3 w-3 animate-spin" /> queueing…
                  </>
                ) : (
                  <>
                    <Wrench className="h-3 w-3" /> backfill
                  </>
                )}
              </button>
              <button
                onClick={loadHlsStatus}
                data-testid="hls-refresh-btn"
                className="font-mono text-[10px] uppercase tracking-widest text-white/40 hover:text-white border border-white/10 hover:border-white/30 px-3 py-1.5 transition-all"
              >
                refresh
              </button>
            </div>
          </div>

          {hlsMsg && (
            <div
              data-testid="hls-msg"
              className={`mt-3 p-2 font-mono text-[11px] border ${
                hlsMsg.ok
                  ? "border-[#39FF14]/40 bg-[#39FF14]/5 text-[#39FF14]"
                  : "border-[#FF3B30]/40 bg-[#FF3B30]/5 text-[#FF3B30]"
              }`}
            >
              {hlsMsg.text}
            </div>
          )}

          {hlsStatus ? (
            <div
              data-testid="hls-stats"
              className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2 font-mono text-[10px] uppercase tracking-widest"
            >
              <div className="border border-[#39FF14]/20 bg-[#39FF14]/5 p-2">
                <p className="text-white/40">ready</p>
                <p className="text-[#39FF14] font-bold text-base tabular-nums">
                  {hlsStatus.ready ?? 0}
                </p>
              </div>
              <div className="border border-[#FFB800]/20 bg-[#FFB800]/5 p-2">
                <p className="text-white/40">generating</p>
                <p className="text-[#FFB800] font-bold text-base tabular-nums">
                  {hlsStatus.generating ?? 0}
                </p>
              </div>
              <div className="border border-white/10 bg-white/[0.02] p-2">
                <p className="text-white/40">pending</p>
                <p className="text-white font-bold text-base tabular-nums">
                  {hlsStatus.pending ?? 0}
                </p>
              </div>
              <div className="border border-[#FF3B30]/20 bg-[#FF3B30]/5 p-2">
                <p className="text-white/40">failed</p>
                <p className="text-[#FF3B30] font-bold text-base tabular-nums">
                  {hlsStatus.failed ?? 0}
                </p>
              </div>
            </div>
          ) : (
            <p className="mt-3 font-mono text-[10px] uppercase tracking-widest text-white/30">
              // loading status…
            </p>
          )}

          {hlsStatus?.failed > 0 && (
            <div className="mt-2">
              <button
                onClick={() => backfillHls({ includeFailed: true })}
                disabled={hlsBackfilling}
                data-testid="hls-retry-failed-btn"
                className="font-mono text-[10px] uppercase tracking-widest text-[#FF3B30] hover:text-white hover:bg-[#FF3B30]/20 border border-[#FF3B30]/30 px-3 py-1.5 transition-all"
              >
                retry {hlsStatus.failed} failed
              </button>
            </div>
          )}

          {/* Nuclear "Repair everything failed/stuck" sweep. Loops through
              the whole queue 25 at a time until it hits zero. Wraps
              reset-and-normalize + reset-hls + backfill-hls (include_failed)
              in one button so admins don't have to hunt through 3 different
              endpoints when a batch upload wedges the pipeline. */}
          <div className="mt-4 border-t border-white/10 pt-4">
            <RepairFailedButton
              runningLabel="Repair failed videos"
              testId="repair-failed-videos-btn"
              onRefreshStatus={loadHlsStatus}
            />
          </div>
        </section>

        {loading && !data ? (
          <div className="py-24 flex items-center justify-center text-white/60">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : data ? (
          <>
            {/* KPI strip */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <KpiCard
                label={`boost revenue · ${days}d`}
                value={fmtUsd(data.boost_revenue?.total_cents)}
                sub={`${data.boost_revenue?.tip_count || 0} tips · ${data.boost_revenue?.unique_tipper_count || 0} tippers`}
                accent="#FF007F"
                icon={DollarSign}
              />
              <KpiCard
                label="videos on watchlist"
                value={fmtInt(data.viral?.length || 0)}
                sub={data.viral?.length ? "trending in window" : "no viral videos yet"}
                accent="#39FF14"
                icon={Sparkles}
              />
              <KpiCard
                label="losing engagement"
                value={fmtInt(data.losing_engagement?.length || 0)}
                sub="≥30% skip rate"
                accent="#FF3B30"
                icon={TrendingDown}
              />
              <KpiCard
                label="top category"
                value={data.categories?.[0]?.category || "—"}
                sub={`${fmtInt(data.categories?.[0]?.engagement || 0)} engagement`}
                accent="#00FFFF"
                icon={BarChart3}
              />
            </div>

            {/* Live pulse — real-time online users + total signups */}
            <LiveStats />

            {/* Rescue Queue — ops actions previously requiring PowerShell */}
            <RescueQueue />

            {/* Categories chart */}
            <SectionCard title="best performing categories" icon={BarChart3} accent="#00FFFF">
              {data.categories?.length ? (
                <div className="h-64 -mx-2">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.categories.slice(0, 8)} margin={{ top: 8, right: 8, bottom: 8, left: 0 }}>
                      <XAxis
                        dataKey="category"
                        stroke="#888"
                        tick={{ fill: "#a0a0a0", fontSize: 10, fontFamily: "monospace" }}
                        tickLine={false}
                      />
                      <YAxis
                        stroke="#888"
                        tick={{ fill: "#a0a0a0", fontSize: 10, fontFamily: "monospace" }}
                        tickLine={false}
                        axisLine={false}
                      />
                      <Tooltip
                        cursor={{ fill: "rgba(255,255,255,0.03)" }}
                        contentStyle={{
                          background: "#0A0A0C",
                          border: "1px solid #FF007F40",
                          fontFamily: "monospace",
                          fontSize: 11,
                        }}
                        labelStyle={{ color: "#FF007F" }}
                      />
                      <Bar dataKey="engagement" radius={[0, 0, 0, 0]}>
                        {data.categories.map((cat, i) => (
                          <Cell key={cat.name || cat.id || i} fill={CATEGORY_COLORS[i % CATEGORY_COLORS.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <p className="text-white/40 py-6 text-center font-mono text-[10px] uppercase tracking-widest">
                  no engagement recorded yet
                </p>
              )}
            </SectionCard>

            {/* Two-column grid of the video tables */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <SectionCard title="most boosted" icon={Zap} accent="#FF007F">
                {data.boosted?.length ? (
                  <div>
                    {data.boosted.map((r) => (
                      <VideoRow
                        key={r.video_id}
                        r={r}
                        right={fmtUsd(r.boost_amount_cents)}
                      />
                    ))}
                  </div>
                ) : null}
              </SectionCard>

              <SectionCard title="highest watch time" icon={Eye} accent="#00FFFF">
                {data.highest_watch_time?.length ? (
                  <div>
                    {data.highest_watch_time.map((r) => (
                      <VideoRow
                        key={r.video_id}
                        r={r}
                        right={fmtSec(r.total_watch_ms)}
                      />
                    ))}
                  </div>
                ) : null}
              </SectionCard>

              <SectionCard title="highest shares" icon={Share2} accent="#39FF14">
                {data.highest_shares?.length ? (
                  <div>
                    {data.highest_shares.map((r) => (
                      <VideoRow
                        key={r.video_id}
                        r={r}
                        right={`${fmtInt(r.total_shares)} shares`}
                      />
                    ))}
                  </div>
                ) : null}
              </SectionCard>

              <SectionCard title="highest comments" icon={MessageCircle} accent="#FFD700">
                {data.highest_comments?.length ? (
                  <div>
                    {data.highest_comments.map((r) => (
                      <VideoRow
                        key={r.video_id}
                        r={r}
                        right={`${fmtInt(r.total_comments)} comments`}
                      />
                    ))}
                  </div>
                ) : null}
              </SectionCard>

              <SectionCard title="viral videos" icon={TrendingUp} accent="#FF007F">
                {data.viral?.length ? (
                  <div>
                    {data.viral.map((r) => (
                      <VideoRow
                        key={r.id}
                        r={{ ...r, video_id: r.id }}
                        right={`${fmtInt(r.engagement)} pts`}
                        subtitle={`${fmtInt(r.view_count)} views`}
                      />
                    ))}
                  </div>
                ) : null}
              </SectionCard>

              <SectionCard title="losing engagement" icon={TrendingDown} accent="#FF3B30">
                {data.losing_engagement?.length ? (
                  <div>
                    {data.losing_engagement.map((r) => (
                      <VideoRow
                        key={r.video_id}
                        r={r}
                        right={`${Math.round((r.skip_rate || 0) * 100)}% skip`}
                        subtitle={`${fmtInt(r.total_views)} views`}
                      />
                    ))}
                  </div>
                ) : null}
              </SectionCard>
            </div>

            {/* Creator performance table */}
            <SectionCard title="creator performance" icon={Trophy} accent="#FFD700">
              {data.creators?.length ? (
                <div className="overflow-x-auto -mx-2">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-white/40 font-mono uppercase tracking-widest text-[10px] border-b border-white/10">
                        <th className="text-left py-2 pl-2">creator</th>
                        <th className="text-right py-2">videos</th>
                        <th className="text-right py-2">views</th>
                        <th className="text-right py-2">likes</th>
                        <th className="text-right py-2">comments</th>
                        <th className="text-right py-2">shares</th>
                        <th className="text-right py-2">saves</th>
                        <th className="text-right py-2 pr-2 text-[#39FF14]">engagement</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.creators.map((c) => (
                        <tr
                          key={c.creator?.user_id || Math.random()}
                          data-testid={`analytics-creator-row-${c.creator?.user_id}`}
                          className="border-b border-white/5 hover:bg-white/[0.02]"
                        >
                          <td className="py-2 pl-2 text-white">
                            <Link
                              to={`/u/${c.creator?.username || ""}`}
                              className="flex items-center gap-2 hover:text-[#00FFFF]"
                            >
                              {c.creator?.picture ? (
                                <img
                                  src={picUrl(c.creator.picture)}
                                  alt=""
                                  className="h-6 w-6 rounded-full object-cover border border-white/15"
                                />
                              ) : (
                                <div className="h-6 w-6 rounded-full bg-[#FF007F]/20 border border-[#FF007F]/40" />
                              )}
                              <span className="font-mono text-xs">
                                @{c.creator?.username || c.creator?.name || "unknown"}
                              </span>
                            </Link>
                          </td>
                          <td className="text-right font-mono text-white/70">{fmtInt(c.videos)}</td>
                          <td className="text-right font-mono text-white/70">{fmtInt(c.views)}</td>
                          <td className="text-right font-mono text-white/70">{fmtInt(c.likes)}</td>
                          <td className="text-right font-mono text-white/70">{fmtInt(c.comments)}</td>
                          <td className="text-right font-mono text-white/70">{fmtInt(c.shares)}</td>
                          <td className="text-right font-mono text-white/70">{fmtInt(c.saves)}</td>
                          <td className="text-right pr-2 font-mono text-[#39FF14] font-bold">
                            {fmtInt(c.engagement)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : null}
            </SectionCard>

            {/* Interest trends */}
            <SectionCard title="user interest trends" icon={Sparkles} accent="#B14BFF">
              {data.interest_trends?.length ? (
                <div className="flex flex-wrap gap-2">
                  {data.interest_trends.slice(0, 24).map((t) => {
                    const [type, ...rest] = t.key.split(":");
                    const label = rest.join(":");
                    const color =
                      type === "category" ? "#FF007F"
                        : type === "hashtag" ? "#00FFFF"
                        : type === "creator" ? "#FFD700"
                        : "#B14BFF";
                    return (
                      <div
                        key={t.key}
                        className="flex items-center gap-2 border px-2.5 py-1 font-mono text-[11px]"
                        style={{ borderColor: color + "60" }}
                        data-testid={`interest-chip-${t.key}`}
                      >
                        <span style={{ color }}>{type}</span>
                        <span className="text-white">{label}</span>
                        <span className="text-white/50">
                          {t.total_score.toFixed(1)} · {t.user_count}u
                        </span>
                      </div>
                    );
                  })}
                </div>
              ) : null}
            </SectionCard>
          </>
        ) : null}
      </div>
    </div>
  );
}
